/**
 * Message Encryption/Decryption Utilities
 *
 * Provides AES-256-GCM encryption/decryption for WhatsApp messages.
 * Uses organization-specific keys for data isolation.
 */

/**
 * Deriva una chiave di cifratura basata su organization_id
 */
async function deriveEncryptionKey(organizationId: string): Promise<CryptoKey> {
  // Usa organization_id + salt fisso per derivare chiave univoca per org
  const baseKey = `${organizationId}-${import.meta.env.VITE_ENCRYPTION_SALT || 'crmobly-default-salt'}`;

  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(baseKey),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const salt = encoder.encode('crmobly-whatsapp-salt-v1');

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Decifra un messaggio cifrato
 * @param encryptedBase64 - Base64 string contenente IV + ciphertext
 * @param organizationId - ID dell'organizzazione per derivare la chiave
 * @returns Testo in chiaro o stringa originale se decifratura fallisce
 */
export async function decryptMessage(
  encryptedBase64: string,
  organizationId: string
): Promise<string> {
  if (!encryptedBase64) return encryptedBase64;

  // Se non sembra base64 cifrato, ritorna così com'è (potrebbe essere già in chiaro)
  if (!isEncrypted(encryptedBase64)) {
    return encryptedBase64;
  }

  try {
    const cryptoKey = await deriveEncryptionKey(organizationId);

    // Decodifica da base64 con gestione errori specifica
    let combined: Uint8Array;
    try {
      combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));
    } catch (atobError) {
      // atob fallisce se la stringa non è base64 valido
      console.warn('[Message Crypto] ⚠️ atob failed - contenuto non è base64 valido, ritorno originale');
      return encryptedBase64;
    }

    // Estrai IV (primi 12 bytes) e ciphertext (resto)
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);

    // Decifra
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      ciphertext
    );

    return new TextDecoder().decode(plaintext);
  } catch (error) {
    // Decifratura fallita è NORMALE per messaggi in chiaro
    if (import.meta.env.DEV) {
      console.debug('[Message Crypto] Decryption failed (expected for plaintext):', error);
    }
    // Se decifratura fallisce, ritorna stringa originale
    // (potrebbe essere un messaggio vecchio non cifrato)
    return encryptedBase64;
  }
}

/**
 * Cifra un messaggio (usato per messaggi in uscita)
 * @param plaintext - Testo in chiaro
 * @param organizationId - ID dell'organizzazione
 * @returns Base64 string cifrata
 */
export async function encryptMessage(
  plaintext: string,
  organizationId: string
): Promise<string> {
  if (!plaintext) return plaintext;

  try {
    const cryptoKey = await deriveEncryptionKey(organizationId);

    // Genera IV random
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Cifra
    const encoder = new TextEncoder();
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoder.encode(plaintext)
    );

    // Combina IV + ciphertext
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertext), iv.length);

    // Converti in base64 con chunking per evitare stack overflow
    let binaryString = '';
    const chunkSize = 8192;
    for (let i = 0; i < combined.length; i += chunkSize) {
      const chunk = combined.subarray(i, Math.min(i + chunkSize, combined.length));
      binaryString += String.fromCharCode(...chunk);
    }

    return btoa(binaryString);
  } catch (error) {
    console.error('[Message Crypto] Encryption failed:', error);
    throw new Error('Failed to encrypt message');
  }
}

/**
 * Verifica se una stringa sembra essere cifrata con AES-GCM
 *
 * Messaggi AES-GCM hanno struttura: 12 bytes IV + ciphertext + 16 bytes tag
 * Minimo teorico: 12 + 1 + 16 = 29 bytes → ~40 caratteri base64
 * In pratica, messaggi utente reali sono più lunghi
 *
 * Soglia: 40 caratteri base64 (~30 bytes) — minimo AES-GCM è 12 IV + 1 cipher + 16 tag = 29 bytes = ~40 char base64.
 * Abbassata da 64 per supportare campi corti cifrati (es. from_name "Luca" → ~44 char base64).
 */
function isEncrypted(text: string): boolean {
  if (!text || text.length < 40) return false;

  // Verifica se sembra base64 (almeno 90% caratteri validi)
  const base64Chars = text.match(/[A-Za-z0-9+/=]/g);
  return base64Chars && base64Chars.length / text.length > 0.9;
}

/**
 * Decifra un messaggio con retry automatico
 * @param encryptedBase64 - Base64 string contenente IV + ciphertext
 * @param organizationId - ID dell'organizzazione per derivare la chiave
 * @param maxRetries - Numero massimo di tentativi (default: 3)
 * @returns Oggetto con { success: boolean, plaintext?: string, error?: string }
 */
export async function decryptMessageWithRetry(
  encryptedBase64: string,
  organizationId: string,
  maxRetries: number = 3
): Promise<{ success: boolean; plaintext?: string; error?: string; attempts: number }> {
  // 🐛 DEBUG: Log input
  console.log('[Message Crypto] 🐛 decryptMessageWithRetry called');
  console.log('[Message Crypto] 🐛 Input (first 100 chars):', encryptedBase64?.substring(0, 100));
  console.log('[Message Crypto] 🐛 OrganizationId:', organizationId?.substring(0, 8) + '...');

  if (!encryptedBase64) {
    console.log('[Message Crypto] 🐛 Empty input, returning as-is');
    return { success: true, plaintext: encryptedBase64, attempts: 0 };
  }

  // Se non sembra base64 cifrato, ritorna così com'è
  const encrypted = isEncrypted(encryptedBase64);
  console.log('[Message Crypto] 🐛 isEncrypted check:', encrypted);

  if (!encrypted) {
    console.log('[Message Crypto] 🐛 Content does NOT look encrypted, returning as-is');
    return { success: true, plaintext: encryptedBase64, attempts: 0 };
  }

  console.log('[Message Crypto] 🐛 Content looks encrypted, proceeding with decryption...');
  let lastError: any = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Delay esponenziale tra i tentativi: 100ms, 300ms, 900ms
      if (attempt > 0) {
        const delay = 100 * Math.pow(3, attempt - 1);
        console.log(`[Message Crypto] Retry ${attempt + 1}/${maxRetries} after ${delay}ms delay`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      const cryptoKey = await deriveEncryptionKey(organizationId);

      // Decodifica da base64
      const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));

      // Estrai IV (primi 12 bytes) e ciphertext (resto)
      const iv = combined.slice(0, 12);
      const ciphertext = combined.slice(12);

      // Decifra
      const plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        ciphertext
      );

      const result = new TextDecoder().decode(plaintext);
      console.log(`[Message Crypto] ✅ Decryption successful on attempt ${attempt + 1}`);
      return { success: true, plaintext: result, attempts: attempt + 1 };
    } catch (error) {
      lastError = error;
      if (import.meta.env.DEV) {
        console.debug(`[Message Crypto] Decryption attempt ${attempt + 1}/${maxRetries} failed (expected for plaintext):`, error);
      }
    }
  }

  // Tutti i tentativi AES-GCM falliti
  if (import.meta.env.DEV) {
    console.debug(`[Message Crypto] All ${maxRetries} AES-GCM decryption attempts failed`);
  }

  // 🔄 FALLBACK: Prova decode base64 semplice (non cifrato)
  // Alcuni messaggi WhatsApp arrivano in base64 grezzo senza cifratura
  try {
    console.log('[Message Crypto] 🔄 Tentativo fallback: decode base64 semplice...');
    const decoded = atob(encryptedBase64);
    const plaintext = new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(decoded, c => c.charCodeAt(0))
    );

    // Verifica che il risultato sia testo leggibile (non binario)
    if (plaintext && plaintext.length > 0 && /[\x20-\x7E\u00A0-\uFFFF]/.test(plaintext)) {
      console.log('[Message Crypto] ✅ Base64 decode successful (messaggio non cifrato)');
      return { success: true, plaintext, attempts: maxRetries + 1 };
    }
  } catch (decodeError) {
    // Non è base64 valido o non è UTF-8 valido
    if (import.meta.env.DEV) {
      console.debug('[Message Crypto] Fallback base64 decode failed:', decodeError);
    }
  }

  // Nessuna strategia ha funzionato
  return {
    success: false,
    error: lastError?.message || 'Decryption failed after all retries',
    attempts: maxRetries
  };
}

/**
 * Decifra tutti i campi content in un array di messaggi
 */
export async function decryptMessages(
  messages: any[],
  organizationId: string
): Promise<any[]> {
  if (!messages || messages.length === 0) return messages;

  const decrypted = await Promise.all(
    messages.map(async (msg) => {
      if (!msg) return msg;

      const decryptedMsg = { ...msg };
      let decryptionFailed = false;

      // Decifra content se presente
      if (msg.content) {
        const originalContent = msg.content;
        decryptedMsg.content = await decryptMessage(msg.content, organizationId);

        // Se il contenuto decifrato è uguale all'originale E sembra essere cifrato (base64 lungo)
        // significa che la decifratura ha fallito
        if (decryptedMsg.content === originalContent &&
            originalContent.length > 50 &&
            /^[A-Za-z0-9+/=]+$/.test(originalContent)) {
          decryptionFailed = true;
        }
      }

      // Decifra body se presente (campo alternativo per il testo del messaggio)
      if (msg.body) {
        const originalBody = msg.body;
        decryptedMsg.body = await decryptMessage(msg.body, organizationId);

        // Verifica anche body per errori di decifratura
        if (decryptedMsg.body === originalBody &&
            originalBody.length > 50 &&
            /^[A-Za-z0-9+/=]+$/.test(originalBody)) {
          decryptionFailed = true;
        }
      }

      // Decifra reply_to_body se presente
      if (msg.reply_to_body) {
        const originalReplyBody = msg.reply_to_body;
        decryptedMsg.reply_to_body = await decryptMessage(msg.reply_to_body, organizationId);

        // Verifica anche reply_to_body per errori di decifratura
        if (decryptedMsg.reply_to_body === originalReplyBody &&
            originalReplyBody.length > 50 &&
            /^[A-Za-z0-9+/=]+$/.test(originalReplyBody)) {
          decryptionFailed = true;
        }
      }

      // Marca il messaggio se la decifratura è fallita
      if (decryptionFailed) {
        decryptedMsg.decryptionFailed = true;
      }

      return decryptedMsg;
    })
  );

  return decrypted;
}
